import Constants from 'expo-constants';
import { router, Stack } from 'expo-router';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, ScrollView, Switch, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';

import { AppText } from '@/components/ui/app-text';
import { Card } from '@/components/ui/card';
import { PrimaryButton } from '@/components/ui/primary-button';
import { ScreenHeader } from '@/components/ui/screen-header';
import { useAppMetaStore } from '@/store/app-meta-store';
import { useAuthStore } from '@/store/auth-store';
import { cloudSyncEnabled, getLastSync, setCloudSyncEnabled, type SyncResult } from '@/sync/cloud-sync';
import { colors, radius, shadows, spacing } from '@/theme/tokens';
import { formatWhenPl } from '@/utils/format';

interface MoreItem {
  icon: keyof typeof Ionicons.glyphMap;
  labelKey: string;
  route?: string;
}

// Grouped per mock wi_cej_menu: tools / people / referral / about-the-app.
const TOOL_ITEMS: MoreItem[] = [
  { icon: 'notifications-outline', labelKey: 'more.reminders', route: '/reminders' },
  { icon: 'people-outline', labelKey: 'more.household', route: '/household' },
  { icon: 'shield-checkmark-outline', labelKey: 'more.protection', route: '/protection' },
  { icon: 'lock-closed-outline', labelKey: 'more.vault', route: '/vault' },
];

const PEOPLE_ITEMS: MoreItem[] = [
  { icon: 'headset-outline', labelKey: 'more.agentInsurance', route: '/agent' },
  { icon: 'cash-outline', labelKey: 'more.creditSpecialist', route: '/agent' },
  { icon: 'chatbubble-ellipses-outline', labelKey: 'more.chat', route: '/chat' },
];

// Global text-size steps (owner: "czy da się pomniejszyć / menu wielkości czcionki").
const FONT_OPTIONS: { value: number; labelKey: string }[] = [
  { value: 0.9, labelKey: 'more.fontSmall' },
  { value: 1, labelKey: 'more.fontDefault' },
  { value: 1.15, labelKey: 'more.fontLarge' },
  { value: 1.3, labelKey: 'more.fontXlarge' },
];

const ABOUT_ITEMS: MoreItem[] = [
  { icon: 'school-outline', labelKey: 'more.school', route: '/school' },
  { icon: 'bulb-outline', labelKey: 'more.ideas', route: '/ideas' },
  { icon: 'information-circle-outline', labelKey: 'more.about', route: '/about' },
  { icon: 'help-circle-outline', labelKey: 'more.whyApp', route: '/why-app' },
  { icon: 'shield-half-outline', labelKey: 'more.privacy', route: '/privacy' },
  { icon: 'document-text-outline', labelKey: 'more.legal', route: '/legal' },
];

/** One tappable menu row; no route yet -> "wkrótce" placeholder. */
function MenuRows({ items }: { items: MoreItem[] }) {
  const { t } = useTranslation();
  return (
    <Card style={{ padding: 0 }}>
      {items.map((item, idx) => (
        <Pressable
          key={item.labelKey}
          accessibilityRole="button"
          onPress={() =>
            item.route
              ? router.push(item.route as never)
              : router.push({ pathname: '/soon', params: { title: t(item.labelKey) } })
          }
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: spacing.sm,
            padding: spacing.md,
            minHeight: 56,
            borderTopWidth: idx === 0 ? 0 : 1,
            borderTopColor: colors.divider,
          }}
        >
          <Ionicons name={item.icon} size={22} color={colors.navy} />
          <View style={{ flex: 1 }}>
            <AppText variant="bodyLg">{t(item.labelKey)}</AppText>
          </View>
          <Ionicons name="chevron-forward" size={18} color={colors.outlineVariant} />
        </Pressable>
      ))}
    </Card>
  );
}

/**
 * "Więcej" menu, since 2026-07-11 opened from the header avatar instead of a
 * bottom tab (the tab slot went to the agent-mode AGENT tab; keeping six tabs
 * was rejected). Slide-from-left echoes a drawer without the drawer's
 * navigation cost. Content unchanged from the old tab.
 */
export default function MenuScreen() {
  const { t } = useTranslation();
  const account = useAuthStore((s) => s.account);
  const logout = useAuthStore((s) => s.logout);
  const restartTutorial = useAppMetaStore((s) => s.restartTutorial);
  const fontScale = useAppMetaStore((s) => s.fontScale);
  const setFontScale = useAppMetaStore((s) => s.setFontScale);
  // Replay the coach guide: reset the flag, then land on Start where it mounts.
  const onReplayTutorial = async () => {
    await restartTutorial();
    router.push('/(tabs)');
  };
  // Logout must be confirmed inline (Alert.alert is a no-op on web): the
  // president tapped "Wyloguj" thinking it just closes the app (2026-07-18),
  // so the confirm spells out what logging out actually does.
  const [confirmLogout, setConfirmLogout] = useState(false);
  // Cloud backup — only meaningful when logged in (needs an account to sync to).
  const [cloudSync, setCloudSync] = useState(cloudSyncEnabled());
  const [syncStatus, setSyncStatus] = useState<SyncResult | null>(getLastSync());
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showToast = (msg: string) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2600);
  };
  // Owner: enabling sync must POP a bottom toast, and the status must say whether
  // it actually worked + when — never let the user assume a sync that never ran.
  const onToggleCloudSync = async (value: boolean) => {
    setCloudSync(value);
    if (!value) {
      await setCloudSyncEnabled(false);
      return;
    }
    showToast(t('more.syncStartedToast'));
    await setCloudSyncEnabled(true);
    const r = getLastSync();
    setSyncStatus(r);
    showToast(r?.ok ? t('more.syncOkToast') : t('more.syncFailToast'));
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={['top', 'bottom']}>
      <Stack.Screen options={{ animation: 'slide_from_left' }} />
      <ScrollView contentContainerStyle={{ padding: spacing.screen, gap: spacing.md, paddingBottom: spacing.xl }}>
        <ScreenHeader title={t('more.title')} />

        {/* Account: the ONLY login entry point in the app — guest mode stays the
            default, so the pitch lives here, not on a splash wall. */}
        {account ? (
          <Card style={{ gap: spacing.sm }}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
              <View
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: radius.full,
                  backgroundColor: colors.navy,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {account.firstName ? (
                  <AppText variant="labelLg" color="#FFFFFF">
                    {account.firstName.charAt(0).toUpperCase()}
                  </AppText>
                ) : (
                  <Ionicons name="person-outline" size={22} color="#FFFFFF" />
                )}
              </View>
              <View style={{ flex: 1 }}>
                <AppText variant="headlineSm">{account.firstName ?? t('more.accountTitle')}</AppText>
                <AppText variant="bodyMd">{account.phone}</AppText>
              </View>
              <View
                style={{
                  backgroundColor: colors.successBg,
                  borderRadius: radius.full,
                  paddingHorizontal: spacing.sm,
                  paddingVertical: 2,
                }}
              >
                <AppText variant="labelMd" color={colors.navy}>
                  {t('more.loggedIn')}
                </AppText>
              </View>
            </View>
            {/* Cloud backup toggle — sits under the phone number, inside the
                account card. Off by default; turning on backs up on-device data
                to the server so it survives a phone change. */}
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                gap: spacing.sm,
                borderTopWidth: 1,
                borderTopColor: colors.divider,
                paddingTop: spacing.sm,
              }}
            >
              <Ionicons name="cloud-upload-outline" size={20} color={colors.navy} />
              <View style={{ flex: 1 }}>
                <AppText variant="bodyLg">{t('more.cloudSyncTitle')}</AppText>
                <AppText variant="bodyMd" color={colors.outline}>
                  {t('more.cloudSyncHint')}
                </AppText>
              </View>
              <Switch
                value={cloudSync}
                onValueChange={onToggleCloudSync}
                // Explicit colors — the platform default track was white-on-white
                // on this card (president couldn't see the toggle, 2026-07-18).
                trackColor={{ false: colors.disabledBg, true: colors.primary }}
                thumbColor="#FFFFFF"
                accessibilityLabel={t('more.cloudSyncTitle')}
              />
            </View>
            {/* Honest sync status: did it work, and when. */}
            {cloudSync && syncStatus ? (
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs, paddingLeft: 28 }}>
                <Ionicons
                  name={syncStatus.ok ? 'checkmark-circle' : 'alert-circle'}
                  size={16}
                  color={syncStatus.ok ? colors.primary : colors.warning}
                />
                <AppText variant="labelMd" color={colors.outline}>
                  {t(syncStatus.ok ? 'more.syncStatusOk' : 'more.syncStatusFail', {
                    when: formatWhenPl(new Date(syncStatus.at).toISOString()),
                  })}
                </AppText>
              </View>
            ) : null}
            {confirmLogout ? (
              <View
                style={{
                  backgroundColor: colors.warningBg,
                  borderRadius: radius.button,
                  padding: spacing.md,
                  gap: spacing.sm,
                }}
              >
                <AppText variant="labelLg" color={colors.navy}>
                  {t('more.logoutConfirmTitle')}
                </AppText>
                <AppText variant="bodyMd" color={colors.textPrimary}>
                  {t('more.logoutConfirmBody')}
                </AppText>
                <PrimaryButton label={t('more.logoutConfirmNo')} onPress={() => setConfirmLogout(false)} />
                <Pressable
                  accessibilityRole="button"
                  onPress={() => logout()}
                  style={({ pressed }) => ({
                    backgroundColor: colors.warning,
                    borderRadius: radius.button,
                    paddingVertical: spacing.md,
                    alignItems: 'center',
                    minHeight: 48,
                    justifyContent: 'center',
                    transform: [{ scale: pressed ? 0.98 : 1 }],
                  })}
                >
                  <AppText variant="labelLg" color="#FFFFFF">
                    {t('more.logoutConfirmYes')}
                  </AppText>
                </Pressable>
              </View>
            ) : (
              <Pressable
                accessibilityRole="button"
                onPress={() => setConfirmLogout(true)}
                style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.xs, minHeight: 44 }}
              >
                <Ionicons name="log-out-outline" size={18} color={colors.warning} />
                <AppText variant="labelLg" color={colors.warning}>
                  {t('more.logout')}
                </AppText>
              </Pressable>
            )}
          </Card>
        ) : (
          <Pressable
            accessibilityRole="button"
            onPress={() => router.push('/auth/login')}
            style={({ pressed }) => ({ transform: [{ scale: pressed ? 0.98 : 1 }] })}
          >
            <Card style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, backgroundColor: colors.infoBg }}>
              <Ionicons name="person-circle-outline" size={40} color={colors.primary} />
              <View style={{ flex: 1 }}>
                <AppText variant="headlineSm">{t('more.loginCta')}</AppText>
                <AppText variant="bodyMd">{t('more.loginCtaSubtitle')}</AppText>
              </View>
              <Ionicons name="chevron-forward" size={18} color={colors.outlineVariant} />
            </Card>
          </Pressable>
        )}

        {/* Text size — shrink or enlarge the whole UI (owner request). */}
        <Card style={{ gap: spacing.sm }}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
            <Ionicons name="text-outline" size={20} color={colors.navy} />
            <AppText variant="bodyLg" style={{ flex: 1 }}>
              {t('more.fontSize')}
            </AppText>
          </View>
          <View style={{ flexDirection: 'row', backgroundColor: colors.infoBg, borderRadius: radius.full, padding: 3 }}>
            {FONT_OPTIONS.map((o) => {
              const active = Math.abs(fontScale - o.value) < 0.001;
              return (
                <Pressable
                  key={o.value}
                  accessibilityRole="button"
                  onPress={() => void setFontScale(o.value)}
                  style={{
                    flex: 1,
                    paddingVertical: spacing.xs,
                    borderRadius: radius.full,
                    backgroundColor: active ? colors.primary : 'transparent',
                    alignItems: 'center',
                  }}
                >
                  {/* Fixed size so the labels don't grow with the very setting
                      they control (owner: „Największa" clipped when selected). */}
                  <AppText
                    variant="labelMd"
                    color={active ? '#FFFFFF' : colors.navy}
                    numberOfLines={1}
                    style={{ fontSize: 12, lineHeight: 16 }}
                  >
                    {t(o.labelKey)}
                  </AppText>
                </Pressable>
              );
            })}
          </View>
        </Card>

        <MenuRows items={TOOL_ITEMS} />
        <MenuRows items={PEOPLE_ITEMS} />

        {/* Referral gets its own card — it's an action, not a setting (mock). */}
        <Pressable
          accessibilityRole="button"
          onPress={() => router.push('/refer')}
          style={({ pressed }) => ({ transform: [{ scale: pressed ? 0.98 : 1 }] })}
        >
          <Card style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
            <Ionicons name="share-social-outline" size={22} color={colors.primary} />
            <View style={{ flex: 1 }}>
              <AppText variant="bodyLg">{t('more.refer')}</AppText>
            </View>
            <Ionicons name="chevron-forward" size={18} color={colors.outlineVariant} />
          </Card>
        </Pressable>

        <MenuRows items={ABOUT_ITEMS} />

        {/* Replay the first-run coach guide (owner request). */}
        <Pressable
          accessibilityRole="button"
          onPress={() => void onReplayTutorial()}
          style={({ pressed }) => ({ transform: [{ scale: pressed ? 0.98 : 1 }] })}
        >
          <Card style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm }}>
            <Ionicons name="sparkles-outline" size={22} color={colors.primary} />
            <View style={{ flex: 1 }}>
              <AppText variant="bodyLg">{t('more.tutorial')}</AppText>
            </View>
            <Ionicons name="chevron-forward" size={18} color={colors.outlineVariant} />
          </Card>
        </Pressable>

        {/* Version footer per mock */}
        <View style={{ alignItems: 'center', gap: 2, paddingTop: spacing.sm }}>
          <AppText variant="bodyMd" color={colors.outlineVariant}>
            {t('more.version', { version: Constants.expoConfig?.version ?? '1.0.0' })}
          </AppText>
          <AppText variant="labelMd" color={colors.outlineVariant} style={{ letterSpacing: 2 }}>
            {t('more.copyright', { year: new Date().getFullYear() })}
          </AppText>
        </View>
      </ScrollView>

      {/* Bottom toast — pops when sync is enabled (owner request). */}
      {toast ? (
        <View
          style={{
            position: 'absolute',
            left: spacing.screen,
            right: spacing.screen,
            bottom: spacing.xl,
            backgroundColor: colors.navy,
            borderRadius: radius.card,
            padding: spacing.md,
            flexDirection: 'row',
            alignItems: 'center',
            gap: spacing.sm,
            ...shadows.card,
          }}
        >
          <Ionicons name="cloud-done-outline" size={20} color="#FFFFFF" />
          <AppText variant="labelLg" color="#FFFFFF" style={{ flex: 1 }}>
            {toast}
          </AppText>
        </View>
      ) : null}
    </SafeAreaView>
  );
}
